<template>
  <div class="external-id">
    <template v-if="ready">
      <template v-if="hasData || forceSelect">
        <AjaxSelect
          v-model="ajaxSelect"
          :api="apiUrl"
          :query="{inventory}"
          :map-fn="(s) => `${s.id} ${s.name} ${this.moneyFormatted(s.takeout)}`"
          :allow-empty-search="false"
          :prop-class="inputClass"
          :select-row-style="selectRowStyle"
          :edge-case-external-id-input="true"
          @changed="onSelected"
          @changedInput="onInput"
        />
        <button class="btn btn-secondary" @click="showExternalItemRawData" v-show="!!ajaxSelect.id && hasData">
          <svgicon src="/icon/pos_invoice.svg" class="icon" width="15" height="15"/>
        </button>
      </template>
      <input v-if="!hasData && !forceSelect" id="txtExternalId" type="text" v-model="stringValue"/>
    </template>
  </div>
</template>

<script>
import {Core} from "@/core/Core";
import AjaxSelect from "@/components/tools/AjaxSelect";
import JsonViewer from "vue-json-viewer";

export default {
  name: "ExternalIDInput",

  components: { AjaxSelect, JsonViewer },

  props: {
    value: {
      type: String|null,
      required: true
    },
    inventory: {
      type: Number,
      required: true
    },
    inputClass: {
      type: String,
      required: false
    },
    forceSelect: {
      type: Boolean,
      required: false
    },

    /* will update prices and name of this item when external item is selected */
    syncItem: {
      type: Object,
      required: false
    },
    syncSilent: {
      type: Boolean,
      default: false
    },

    /* avoid initial loading in AjaxSelect */
    prefillInitialData: {
      type: Object,
      default: () => ({}),
    }
  },

  data() {
    return {
      ajaxSelect: {
        id: parseInt(this.value) || null,
        ...this.prefillInitialData
      },
      selectRowStyle: {
        padding: '4px 16px',
        'font-size': 'smaller',
      },
      ready: false,
      hasData: false,
      stringValue: this.value,
      apiUrl: '/api/entity/external_item/select',
      lastInternallyEmittedValue: null,
      emptyValue: this.value === '' ? '' : null, /* when value is returning to 'empty', either null or '' */
      itemDataCache: null,
      refreshingPosInventory: false,
    };
  },

  async mounted() {
    if (!this.forceSelect) {
      await this.validateIfHasData();
    }
    this.ready = true;
  },

  watch: {
    value(value) {
      /* catch externally changed value */
      if (value !== this.lastInternallyEmittedValue) {
        this.ajaxSelect = {
          id: parseInt(value) || null,
        }
        this.stringValue = value;
      }
    },
    stringValue(value) {
      this.$emit('input', value);
    }
  },

  methods: {
    moneyFormatted(v) {
      let asFloat = parseFloat(v);
      return isNaN(asFloat) ? '' : `(${asFloat.toFixed(2)}$)`;
    },

    updateInputValue(value) {
      if (!value) {
        value = this.emptyValue;
      } else if (typeof value !== 'string') {
        value = value.toString();
      }
      this.lastInternallyEmittedValue = value;
      this.$emit('input', value);
    },

    onInput(val) {
      /* only take the first part of the input, everything before the first space */
      if (val) {
        let i = val.indexOf(' ');
        if (i !== -1) {
          val = val.substring(0, i);
        }
        val = val.trim();
      }
      this.updateInputValue(val);
    },

    async validateIfHasData() {
      let response = await Core.noThrowRequest("GET", this.apiUrl, { inventory: this.inventory, limit: 1 });
      this.hasData = response.length > 0;
    },

    onSelected(id) {
      this.updateInputValue(id);

      let externalItem = this.ajaxSelect;
      let itemToUpdate = this.syncItem;
      if (itemToUpdate) {
        let changed = [];

        /* update name, only when name is empty */
        if (externalItem.name && itemToUpdate.name) {
          let formatted = externalItem.name.trim().replace(/\s\s+/g, ' ')
          formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1).toLowerCase()
          for (let language of Object.keys(itemToUpdate.name)) {
            let oldName = itemToUpdate.name[language];
            let newName = formatted;
            if (!oldName) {
              itemToUpdate.name[language] = newName;
              changed.push({
                k: `${this.$t("items.name")} (${language.toLocaleUpperCase()})`,
                o: oldName,
                n: newName
              });
            }
          }
        }
        /* update prices */
        for (let scheme of itemToUpdate.priceSchemes || []) {
          let method = scheme.method;
          let oldUnitPrice = scheme.unitPrice;
          let newUnitPrice = parseFloat(externalItem[method]);
          if (!isNaN(newUnitPrice) && oldUnitPrice !== newUnitPrice) {
            scheme.unitPrice = newUnitPrice;
            changed.push({
              k: this.$t(`scheme_methods.${method}`),
              o: this.$tc(oldUnitPrice),
              n: this.$tc(newUnitPrice)
            });
          }
        }

        /* notification */
        if (!this.syncSilent && changed.length) {
          let msg = [`<b>${this.$t('external_id_input.data_modified')}</b><br>`]
              .concat(...changed.map(({k, o, n}) => `<b>${k}:</b> ${o} -> ${n}`))
              .concat(`<br>${this.$t("external_id_input.save")}`)
              .join('<br>');

          toast(msg, 'info');
        }

      }
    },

    async showExternalItemRawData() {
      let itemData;
      let externalID = this.ajaxSelect.id;

      if (this.itemDataCache && this.itemDataCache.id === externalID) {
        itemData = this.itemDataCache;
      } else {
        itemData = await Core.noThrowRequest('GET', `/api/inventory/${this.inventory}/external_items/${externalID}`);
        if (!itemData) {
          return;
        }
        this.itemDataCache = itemData;
      }
      this.$store.state.modalComponent = JsonViewer
      this.$store.state.modalComponentProps = {
        value: itemData.rawData,
        expanded: true,
        copyable: true,
        boxed: true,
        "expand-depth": 5
      }
      this.$modal.show('component-modal');
    }
  }
}
</script>

<style lang="scss" scoped>
.external-id {
  display: flex;

  .ajax-select, #txtExternalId {
    flex-grow: 1;
  }
}

.btn {
  margin-left: 8px;
}
</style>